import Card from 'components/card';
import { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';

type ChartThemeKey = 'purple' | 'yellow' | 'green' | 'blue' | 'brown';

const CHART_THEME: Record<
    ChartThemeKey,
    {
        bar: string;
        activePeriodClass: string;
        growthClass: string;
    }
> = {
    purple: {
        bar: '#1C0F53',
        activePeriodClass:
            'border-leadrs-purple-focus bg-leadrs-purple-soft text-leadrs-purple-focus',
        growthClass: 'text-leadrs-purple-focus',
    },
    yellow: {
        bar: '#1D1A01',
        activePeriodClass:
            'border-leadrs-yellow-focus bg-leadrs-yellow-soft text-leadrs-yellow-focus',
        growthClass: 'text-leadrs-yellow-focus',
    },
    green: {
        bar: '#173616',
        activePeriodClass:
            'border-leadrs-green-focus bg-leadrs-green-soft text-leadrs-green-focus',
        growthClass: 'text-leadrs-green-focus',
    },
    blue: {
        bar: '#0E375C',
        activePeriodClass:
            'border-leadrs-blue-focus bg-leadrs-blue-soft text-leadrs-blue-focus',
        growthClass: 'text-leadrs-blue-focus',
    },
    brown: {
        bar: '#5B3B0C',
        activePeriodClass:
            'border-leadrs-brown-focus bg-leadrs-brown-soft text-leadrs-brown-focus',
        growthClass: 'text-leadrs-brown-focus',
    },
};

export function transformMongoAggregateToApexChart(
    data: any,
    type: any = 'monthly',
    calculateCumulativeSum = true,
    barColor = '#1C0F53'
) {
    data.sort((a: any, b: any) => {
        const [monthA, yearA] = a._id.split('-').map(Number);
        const [monthB, yearB] = b._id.split('-').map(Number);
        return yearA !== yearB ? yearA - yearB : monthA - monthB;
    });

    const aggregatedData: any[] = [];
    let cumulativeSum = 0;
    let values: any[] = [];
    let currentPeriod = '';

    data.forEach((item: any) => {
        const [month, year] = item._id.split('-').map(Number);
        let periodKey: any;

        switch (type) {
            case 'monthly':
                periodKey = `${month}-${year}`;
                break;
            case 'quarterly':
                periodKey = `Q${Math.ceil(month / 3)}-${year}`;
                break;
            case 'yearly':
                periodKey = `${year}`;
                break;
        }

        if (currentPeriod !== periodKey) {
            currentPeriod = periodKey;
            cumulativeSum = 0;
            values = [];
        }

        cumulativeSum += item.sum;
        values.push(item.sum);

        const existingEntry = aggregatedData.find((d) => d._id === periodKey);
        if (existingEntry) {
            existingEntry.sum = cumulativeSum;
            existingEntry.values = values;
            existingEntry.average = cumulativeSum / values.length;
        } else {
            aggregatedData.push({
                _id: periodKey,
                sum: cumulativeSum,
                values,
                average: cumulativeSum / values.length,
            });
        }
    });

    return {
        series: [
            {
                name: 'Total',
                data: calculateCumulativeSum
                    ? aggregatedData.map((item: any) => item.sum)
                    : aggregatedData.map((item: any) => item.average),
            },
        ],
        options: {
            chart: {
                type: 'bar',
                toolbar: { tools: { download: false } },
                dropShadow: { enabled: false },
            },
            colors: [barColor],
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: '55%',
                    borderRadius: 8,
                    borderRadiusApplication: 'end',
                },
            },
            dataLabels: { enabled: false },
            stroke: {
                show: true,
                width: 2,
                colors: ['transparent'],
            },
            tooltip: {
                theme: 'light',
                style: {
                    fontFamily: 'Geist, sans-serif',
                },
            },
            xaxis: {
                categories: aggregatedData.map((item: any) => {
                    const [period, year] = item._id.split('-');
                    switch (type) {
                        case 'monthly':  return `${period.padStart(2, '0')}/${year}`;
                        case 'quarterly': return `${period} ${year}`;
                        case 'yearly':   return period;
                    }
                }),
                labels: { style: { colors: '#676767', fontFamily: 'Geist, sans-serif', fontSize: '12px' } },
                axisBorder: {
                    color: '#F3EDE4',
                },
                axisTicks: {
                    color: '#F3EDE4',
                },
            },
            yaxis: {
                labels: {
                    formatter: (val: any) => parseInt(val),
                    style: { colors: '#676767', fontFamily: 'Geist, sans-serif', fontSize: '12px' },
                },
            },
            grid: { borderColor: '#F3EDE4', strokeDashArray: 4 },
        },
    };
}

function ChartBar3Period(props: {
    title: string;
    data: any;
    calculateCumulativeSum: boolean;
    chartTheme?: ChartThemeKey;
}) {
    const { title, data, calculateCumulativeSum, chartTheme = 'purple' } = props;
    const [state, setState] = useState(null);
    const [period, setPeriod] = useState('monthly');
    const theme = CHART_THEME[chartTheme];

    useEffect(() => {
        if (!data || !data.length) return;
        setState(
            transformMongoAggregateToApexChart(
                data,
                period,
                calculateCumulativeSum,
                theme.bar
            )
        );
    }, [data, period, calculateCumulativeSum, theme.bar]);

    const periods = [
        { key: 'monthly',   label: 'Mensuel' },
        { key: 'quarterly', label: 'Trimestriel' },
        { key: 'yearly',    label: 'Annuel' },
    ];

    return (
        <Card extra="relative border border-leadrs-stroke bg-white p-5">
            <div className="flex items-start justify-between">
                <div>
                    <h4 className="font-geist text-base font-semibold text-leadrs-text">{title}</h4>
                    {data && data.length > 1 && state && (() => {
                        const d = state.series[0].data;
                        const previous = d[d.length - 2];
                        const growthRate = previous
                            ? ((d[d.length - 1] - previous) / previous) * 100
                            : 0;
                        const isPositive = growthRate >= 0;
                        const periodLabel: any = { monthly: 'ce mois', quarterly: 'ce trimestre', yearly: 'cette année' };
                        return (
                            <p className={`mt-1 font-geist text-[13px] font-medium ${isPositive ? theme.growthClass : 'text-leadrs-red-focus'}`}>
                                {isPositive && '+'}{growthRate.toFixed(2)}% {periodLabel[period]}
                            </p>
                        );
                    })()}
                </div>
                <div className="flex gap-2">
                    {periods.map(({ key, label }) => (
                        <button
                            key={key}
                            onClick={() => setPeriod(key)}
                            className={`rounded-lg border px-2.5 py-1 font-geist text-xs font-medium transition-colors ${
                                period === key
                                    ? `${theme.activePeriodClass}`
                                    : 'border-leadrs-stroke bg-white text-leadrs-text-soft hover:text-leadrs-text'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {state ? (
                <div id="chart" className="mt-2">
                    <ReactApexChart
                        options={state.options as any}
                        series={state.series}
                        type="bar"
                        height={250}
                    />
                </div>
            ) : (
                <div className="flex h-64 items-center justify-center">
                    <p className="font-geist text-sm text-leadrs-text-soft">
                        {data && data.length === 0 ? 'Aucune donnée disponible' : 'Chargement...'}
                    </p>
                </div>
            )}
        </Card>
    );
}

export default ChartBar3Period;
